import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { useDownloadExport } from '@/features/analytics/api'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'
import type { Alert, ClosingReport } from '../types'

/**
 * Le bilan, et le geste qui arrête les comptes.
 *
 * Cet écran se lit une fois, le lendemain, et il s'imprime. D'où sa forme :
 * des sections dans l'ordre où on se pose les questions — qui est venu,
 * combien on a encaissé, combien on a dépensé, ce qu'il reste — plutôt qu'un
 * tableau de bord à surveiller.
 *
 * Deux chiffres sont présentés **côte à côte et jamais additionnés**, parce
 * que c'est la confusion la plus fréquente à la lecture d'un bilan :
 *
 *   Budget restant = prévisionnel − dépenses. Ignore les recettes.
 *   Résultat       = recettes nettes − dépenses. Ignore le budget.
 *
 * La clôture arrive en dernier, après le bilan et non avant : on ne fige pas
 * des comptes qu'on n'a pas lus.
 */
export function AdminClosingPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.closing')
  }, [t])

  const report = useAdminQuery<ClosingReport>(
    ['admin-closing-report', eventId, i18n.language],
    `/admin/events/${eventId}/closing/report?locale=${i18n.language}`,
    eventId,
  )
  const blockers = useAdminQuery<Alert[]>(
    ['admin-closing-blockers', eventId],
    `/admin/events/${eventId}/closing/blockers`,
    eventId,
  )

  const close = useAdminMutation<{ status: string }, { force: boolean }>(
    'post',
    (variables) => `/admin/events/${eventId}/close?force=${variables.force}`,
    eventId,
    [
      ['admin-closing-report', eventId, i18n.language],
      ['admin-closing-blockers', eventId],
      ['admin-event', eventId],
    ],
  )

  const data = report.data
  const currency = data?.currency ?? 'EUR'
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency }, i18n.language)

  /**
   * Le CSV part avec le jeton : on ne peut pas se contenter d'un lien.
   *
   * Il passait par le client JSON, qui lisait la réponse comme du JSON : le
   * fichier enregistré contenait « null ». Le téléchargement brut est celui
   * des exports du bilan, et son échec s'affiche au lieu de rester muet.
   */
  const exporter = useDownloadExport(eventId)
  const download = () => exporter.mutate({ key: 'closing' })
  const downloading = exporter.isPending

  if (report.isPending) {
    return (
      <>
        <AdminHeader title={t('admin.section.closing')} />
        <Skeleton className="mt-6 h-64 w-full" />
      </>
    )
  }

  if (!data) return <AdminHeader title={t('admin.section.closing')} />

  const isClosed = data.event_status === 'closed' || data.event_status === 'archived'
  const pending = blockers.data ?? []

  return (
    <>
      <AdminHeader title={t('admin.section.closing')} />

      <p className="mt-2 text-sm text-content-muted">
        {t('admin.closing.generatedAt', {
          at: new Date(data.generated_at).toLocaleString(i18n.language),
        })}
      </p>

      {/* Les deux chiffres qui se ressemblent, séparés dès le premier écran. */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Headline
          label={t('admin.closing.result')}
          hint={t('admin.closing.resultHint')}
          value={money(data.result_minor)}
          tone={data.result_minor >= 0 ? 'good' : 'bad'}
        />
        <Headline
          label={t('admin.closing.budgetRemaining')}
          hint={t('admin.closing.budgetRemainingHint')}
          value={money(data.budget_remaining_minor)}
          tone={data.budget_remaining_minor >= 0 ? 'good' : 'bad'}
        />
      </div>

      <Section title={t('admin.closing.attendance')}>
        <div className="grid gap-4 sm:grid-cols-4">
          <Figure label={t('admin.closing.ticketsSold')} value={data.attendance.tickets_sold} />
          <Figure
            label={t('admin.closing.expected')}
            value={data.attendance.attendees_expected}
          />
          <Figure
            label={t('admin.closing.arrived')}
            value={data.attendance.attendees_checked_in}
          />
          <Figure
            label={t('admin.closing.rate')}
            value={`${data.attendance.attendance_percent} %`}
          />
        </div>
      </Section>

      <Section title={t('admin.closing.revenue')}>
        <div className="grid gap-4 sm:grid-cols-4">
          <Figure label={t('admin.closing.gross')} value={money(data.revenue_gross_minor)} />
          <Figure label={t('admin.closing.refunded')} value={money(data.refunded_minor)} />
          <Figure label={t('admin.closing.net')} value={money(data.revenue_net_minor)} />
          <Figure
            label={t('admin.closing.outstanding')}
            value={money(data.outstanding_minor)}
          />
        </div>

        <Table
          head={[t('admin.closing.category'), t('admin.closing.amount')]}
          rows={data.revenue_lines.map((line) => [line.label, money(line.amount_minor)])}
        />
        <Table
          head={[t('admin.closing.method'), t('admin.closing.amount')]}
          rows={data.methods.map((line) => [
            t(`pos.methods.${line.label}`, { defaultValue: line.label }),
            money(line.amount_minor),
          ])}
        />
      </Section>

      <Section title={t('admin.closing.expenses')}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Figure label={t('admin.closing.budget')} value={money(data.budget_minor)} />
          <Figure label={t('admin.closing.spent')} value={money(data.spent_minor)} />
          <Figure
            label={t('admin.closing.budgetRemaining')}
            value={money(data.budget_remaining_minor)}
          />
        </div>
        <Table
          head={[
            t('admin.closing.item'),
            t('admin.closing.planned'),
            t('admin.closing.spent'),
            t('admin.closing.left'),
          ]}
          rows={data.budget_lines.map((line) => [
            line.label,
            money(line.planned_minor),
            money(line.spent_minor),
            money(line.remaining_minor),
          ])}
        />
      </Section>

      {data.cash_sessions.length > 0 ? (
        <Section title={t('admin.closing.tills')}>
          <Table
            head={[
              t('admin.closing.till'),
              t('admin.closing.heldBy'),
              t('admin.closing.expectedAmount'),
              t('admin.closing.counted'),
              t('admin.closing.variance'),
              t('admin.closing.note'),
            ]}
            rows={data.cash_sessions.map((line) => [
              line.register_label,
              line.user_label,
              money(line.expected_minor),
              line.counted_minor === null ? '—' : money(line.counted_minor),
              line.variance_minor === null ? '—' : money(line.variance_minor),
              line.closing_note ?? '',
            ])}
          />
          <p className="mt-2 text-sm text-content-secondary">
            {t('admin.closing.totalVariance')} :{' '}
            <strong className="tabular-nums">{money(data.cash_variance_minor)}</strong>
          </p>
        </Section>
      ) : null}

      {/* Des espèces rendues sans qu'aucun tiroir ouvert ne les donne : déjà
          déduites des recettes, absentes des théoriques. Les nommer évite de
          les chercher dans les écarts de caisse. */}
      {data.cash_refunds_outside_tills_minor > 0 ? (
        <p className="mb-8 text-sm text-content-secondary">
          {t('admin.closing.cashRefundsOutsideTills')} :{' '}
          <strong className="tabular-nums">
            {money(data.cash_refunds_outside_tills_minor)}
          </strong>
        </p>
      ) : null}

      {data.leftovers.length > 0 ? (
        <Section title={t('admin.closing.leftovers')}>
          <Table
            head={[
              t('admin.closing.product'),
              t('admin.closing.remaining'),
              t('admin.closing.sold'),
            ]}
            rows={data.leftovers.map((line) => [line.label, line.remaining, line.sold])}
          />
        </Section>
      ) : null}

      {/* La clôture en dernier : on ne fige pas des comptes qu'on n'a pas lus. */}
      <Card className="mt-10">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.closing.closeTitle')}
        </h2>
        <p className="mt-2 text-sm text-content-secondary">{t('admin.closing.closeBody')}</p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" disabled={downloading} onClick={download}>
            {t('admin.closing.download')}
          </Button>
          {exporter.isError ? (
            <span className="text-sm text-state-danger">{t('admin.closing.downloadFailed')}</span>
          ) : null}

          {isClosed ? (
            <Badge tone="success">{t('admin.closing.alreadyClosed')}</Badge>
          ) : (
            <Button
              size="sm"
              disabled={close.isPending}
              onClick={() => close.mutate({ force: false })}
            >
              {t('admin.closing.closeAction')}
            </Button>
          )}
        </div>

        {!isClosed && pending.length > 0 ? (
          <div className="mt-5 border-l-2 border-l-ember-400 pl-3">
            <p className="text-sm text-content-primary">{t('admin.closing.blockedTitle')}</p>
            <ul className="mt-2 space-y-1">
              {pending.map((alert) => (
                <li key={alert.code} className="text-sm text-content-secondary">
                  {t(`admin.alerts.code.${alert.code}.title`, {
                    defaultValue: alert.title,
                    count: alert.count,
                  })}
                  {alert.count > 1 ? (
                    <span className="font-mono text-caption text-content-muted"> ×{alert.count}</span>
                  ) : null}
                  <BlockerDetail alert={alert} money={money} />
                </li>
              ))}
            </ul>
            {/* Passer outre reste possible — l'équipe connaît sa soirée mieux
                que le logiciel — mais c'est nommé, et tracé au nom de qui le fait. */}
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              disabled={close.isPending}
              onClick={() => close.mutate({ force: true })}
            >
              {t('admin.closing.forceAction')}
            </Button>
          </div>
        ) : null}

        {close.isError ? (
          <p className="mt-4 text-sm text-ember-300">{close.error.message}</p>
        ) : null}
      </Card>
    </>
  )
}

/**
 * Qui tient encore une caisse, qui a encore de l'argent en poche.
 *
 * Un blocage de caisse se règle avec quelqu'un, pas avec un compteur : le nom
 * et le montant évitent d'aller chercher l'information sur un autre écran au
 * moment précis où l'on veut clôturer.
 */
function BlockerDetail({ alert, money }: { alert: Alert; money: (minor: number) => string }) {
  const { t, i18n } = useTranslation()

  if (alert.code === 'cash.session_open') {
    const sessions = (alert.context.sessions ?? []) as {
      register_label: string
      user_label: string
      opened_at: string | null
    }[]
    return (
      <ul className="mt-1 space-y-0.5 pl-3">
        {sessions.map((row, index) => (
          <li key={index} className="text-caption text-content-muted">
            {t('admin.closing.openTill', {
              register: row.register_label,
              who: row.user_label,
              at: row.opened_at
                ? new Date(row.opened_at).toLocaleString(i18n.language)
                : '—',
            })}
          </li>
        ))}
      </ul>
    )
  }

  if (alert.code === 'cash.held_by_staff') {
    const holders = (alert.context.holders ?? []) as { user_label: string; held_minor: number }[]
    return (
      <ul className="mt-1 space-y-0.5 pl-3">
        {holders.map((row, index) => (
          <li key={index} className="flex justify-between gap-3 text-caption text-content-muted">
            <span className="min-w-0 truncate">{row.user_label}</span>
            <span className="shrink-0 tabular-nums">{money(row.held_minor)}</span>
          </li>
        ))}
      </ul>
    )
  }

  return null
}

function Headline({
  label,
  hint,
  value,
  tone,
}: {
  label: string
  hint: string
  value: string
  tone: 'good' | 'bad'
}) {
  return (
    <Card>
      <p className="overline text-content-muted">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-display-sm tabular-nums',
          tone === 'good' ? 'text-content-primary' : 'text-ember-300',
        )}
      >
        {value}
      </p>
      <p className="mt-2 text-xs text-content-muted">{hint}</p>
    </Card>
  )
}

function Figure({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="overline text-content-muted">{label}</p>
      <p className="mt-1 font-display text-heading-md tabular-nums text-content-primary">
        {value}
      </p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-heading-md text-content-primary">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  )
}

function Table({
  head,
  rows,
}: {
  head: string[]
  rows: (string | number)[][]
}) {
  if (rows.length === 0) return null

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] text-sm">
        <thead>
          <tr className="border-b border-line-subtle text-left">
            {head.map((cell) => (
              <th key={cell} className="py-2 pr-4 font-normal text-content-muted">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-line-subtle last:border-0">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn(
                    'py-2 pr-4 text-content-secondary',
                    cellIndex > 0 && 'tabular-nums',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
